import { SESSION_STORAGE_KEYS } from '@chegg-wt/core/utils/sessionStorage'
import { AppState, LogicDeps } from '@chegg-wt/core/state'
import { navigated, flowed } from '@chegg-wt/core/state/app/actions'
import { createLogic } from 'redux-logic'

import { FSACreatorPayload } from '@chegg-wt/core/state/types'
import { searchSources } from '@chegg-wt/core/api/source'
import {
  emitSuccessEvent,
  WORLDCAT_API_REQUEST,
} from '@chegg-wt/core/state/instrumentation/actions'
import {
  evalSourceTypes,
  hasWorldcatLink,
  isAutociteUrl,
  isWrongTypeError,
  getSourceType,
  getAutociteUrl,
  hasAutociteUrl,
} from '@chegg-wt/core/utils/searchSources'
import { sendPlusCiteRioEvent } from '@chegg-wt/core/api/plusCiteRioEvent'
import { getClientCommonFields } from '@chegg-wt/core/utils/plusCiteRioEvent'
import {
  softLoggedInSelector,
  jwtSelector,
  isProSelector,
} from '@chegg-wt/core/state/user/selectors'
import cloneDeep from 'lodash.clonedeep'
import { getCitationTitle, replaceCitationTitle } from '@chegg-wt/core/utils/citations'
import { bff } from '@chegg-wt/core/api/bff'
import {
  citationToFormStateTransformation,
  formStateToCitationTransformation,
} from '@chegg-wt/core/components/Layouts/Form/utils'
import {
  citationCreate,
  citationCreateInProgress,
  citationValidated,
} from '../citation/actions'
import { AddRemoveCitationInProgress } from '../citation/types'
import {
  autociteApiVersionSelector,
  autociteV0RioOptFlagSelector,
  createCitationQuerySelector,
  createCitationSourceTypeSelector,
  sourceTypeManualSelector,
} from './selectors'
import { ActionTypes } from './constants'

import {
  journalEngineSelected,
  sourceSearchFailed,
  sourceSearchResultsPageLoaded,
  sourceSearchSucceeded,
  sourceSelected,
  sourceTypeSelected,
  sourceQueryEntered,
  sourceSelectionCompleted,
  sourceSelectionAutociteRequestFailed,
  oneClickCitation,
} from './actions'

export const sourceSearchLogic = createLogic<
  AppState,
  ReturnType<typeof sourceSearchResultsPageLoaded>['payload'],
  any,
  LogicDeps
>({
  type: ActionTypes.CREATE_CITATION_SEARCH_RESULTS_PAGE_LOADED,
  async process({ action, routes, getState }, dispatch, done) {
    const {
      sourceType,
      query: searchQuery,
      style,
      crossrefResultCountEnabled,
    } = action.payload
    try {
      const state = getState()
      const { cheggUserUuid } = jwtSelector(state) || {}
      const autociteApiVersion = autociteApiVersionSelector(state)
      const sources = await searchSources({
        sourceType,
        searchQuery,
        autociteApiVersion,
        clientCommonFields: {
          softLoggedIn: softLoggedInSelector(state),
          cheggUserUuid,
        },
        crossrefResultCountEnabled,
      })
      dispatch(sourceSearchSucceeded(sources))

      if (hasWorldcatLink(sourceType)) {
        dispatch(emitSuccessEvent({ viewName: WORLDCAT_API_REQUEST, value: sourceType }))
      }
    } catch (error) {
      dispatch(sourceSearchFailed(error))
      if (isAutociteUrl(sourceType, searchQuery) && isWrongTypeError(error)) {
        dispatch(navigated(routes.toManualCitation({ style, sourceType })))
      }
    } finally {
      done()
    }
  },
})

export const journalSearchLogic = createLogic<
  AppState,
  ReturnType<typeof journalEngineSelected>['payload'],
  any,
  LogicDeps
>({
  type: ActionTypes.CREATE_CITATION_JOURNAL_TAB_SELECTED,
  async process({ action, getState }) {
    const { type: journalType, crossrefResultCountEnabled } = action.payload
    const state = getState()
    const { cheggUserUuid } = jwtSelector(state) || {}
    const searchQuery = createCitationQuerySelector(state)
    const sourceType = createCitationSourceTypeSelector(state)
    const autociteApiVersion = autociteApiVersionSelector(state)

    try {
      const sources = await searchSources({
        searchQuery,
        sourceType,
        autociteApiVersion,
        clientCommonFields: {
          softLoggedIn: softLoggedInSelector(state),
          cheggUserUuid,
        },
        journalType,
        crossrefResultCountEnabled,
      })
      return sourceSearchSucceeded(sources)
    } catch (error) {
      return sourceSearchFailed(error)
    }
  },
})

export const sourceTypeSelectedLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof sourceTypeSelected>,
  any,
  LogicDeps
>({
  type: [sourceTypeSelected],
  validate({ action }, allow, reject) {
    const { redirectTo } = action.payload
    if (redirectTo) {
      allow(action)
    } else {
      reject(action)
    }
  },
  process({ action, getState }, dispatch, done) {
    const state = getState()
    const manual = sourceTypeManualSelector(state)
    const { redirectTo } = action.payload

    if (manual) {
      dispatch(flowed('toManualCitation'))
    } else {
      dispatch(flowed(redirectTo))
    }
    return done()
  },
})

export const sourceQueryEnteredLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof sourceQueryEntered>,
  any,
  LogicDeps
>({
  type: [sourceQueryEntered],
  process({ getState }) {
    const manual = sourceTypeManualSelector(getState())
    if (manual) {
      return flowed('toManualCitation')
    }
    return flowed('toSourceSelection')
  },
})

export const sourceSelectedLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof sourceSelected>,
  any,
  LogicDeps
>({
  type: [sourceSelected],
  validate({ action }, allow, reject) {
    const { selectedSource, searchQuery, index } = action.payload
    if (hasAutociteUrl(selectedSource)) {
      allow(action)
    } else {
      reject(sourceSelectionCompleted(selectedSource, searchQuery, index))
    }
  },
  async process({ action, getState }, dispatch, done) {
    const { selectedSource, searchQuery, index } = action.payload
    const sourceType = getSourceType(selectedSource)
    const autociteUrl = getAutociteUrl(selectedSource)
    const state = getState()
    const isPro = isProSelector(state)

    try {
      if (isPro) {
        dispatch(citationCreateInProgress(index, AddRemoveCitationInProgress.ADD))
      }
      const { cheggUserUuid } = jwtSelector(state) || {}
      const autociteApiVersion = autociteApiVersionSelector(state)
      const sources = await searchSources({
        searchQuery: autociteUrl,
        sourceType,
        autociteApiVersion,
        clientCommonFields: {
          softLoggedIn: softLoggedInSelector(state),
          cheggUserUuid,
        },
      })
      // We expect only one result returned
      const sourcesLength = sources.length
      if (sourcesLength === 1) {
        dispatch(sourceSelectionCompleted(sources[0], searchQuery, index))
        done()
      }
      const incorrectSourceResultError = new Error(
        `Incorrect result count ${sourcesLength} returned, expect 1`
      )
      dispatch(sourceSelectionAutociteRequestFailed(incorrectSourceResultError))
    } catch (error) {
      dispatch(sourceSelectionAutociteRequestFailed(error))
    }

    dispatch(sourceSelectionCompleted(selectedSource, searchQuery, index))
    done()
  },
})

export const sourceSelectionCompletedLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof sourceSelectionCompleted>,
  any,
  LogicDeps
>({
  type: [sourceSelectionCompleted],
  async process({ action, getState }, dispatch, done) {
    const state = getState()
    const isPro = isProSelector(state)
    const { selectedSource, searchQuery, index } = action.payload
    if (selectedSource.autociteRioTrackingID) {
      const { cheggUserUuid } = jwtSelector(getState()) || {}
      const state = getState()
      const autociteV0RioOptFlag = autociteV0RioOptFlagSelector(state)
      const autociteApiVersion = autociteApiVersionSelector(state)
      if (autociteApiVersion === 'v0' || autociteV0RioOptFlag) {
        sendPlusCiteRioEvent(
          selectedSource.autociteResponseForRio,
          selectedSource.numFieldsAutocite,
          selectedSource.autociteRioTrackingID,
          getClientCommonFields(softLoggedInSelector(getState()), cheggUserUuid)
        )
      }
    }
    const source = createCitationSourceTypeSelector(state)
    const shouldSkipEvalPage = !evalSourceTypes.includes(source)
    const saveData = {
      ...selectedSource,
      searchQuery,
    }
    sessionStorage.setItem(SESSION_STORAGE_KEYS.searchData, JSON.stringify(saveData))
    if (isPro) {
      dispatch(oneClickCitation(selectedSource, index))
    } else if (shouldSkipEvalPage) dispatch(flowed('toNewCitation'))
    else dispatch(flowed('toSourceConfirmation'))
    done()
  },
})

export const oneClickCitationLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof oneClickCitation>,
  any,
  LogicDeps
>({
  type: [oneClickCitation],
  async process({ action }, dispatch, done) {
    const { source, index } = action.payload
    dispatch(citationCreateInProgress(index, AddRemoveCitationInProgress.ADD))
    const sourceDataClone = cloneDeep(source)
    let title = getCitationTitle(source.data as any)
    if (title) {
      ;[title] = await bff.capitalizeCitations({
        citationsInput: [title],
      })
      sourceDataClone.data = replaceCitationTitle(
        cloneDeep(source.data) as any,
        title
      ) as any
    }

    const citation = citationToFormStateTransformation(
      formStateToCitationTransformation(sourceDataClone.data)
    )
    const { validatorStatus, ...restCitationData } = citation
    dispatch(citationValidated(validatorStatus))
    dispatch(citationCreate(restCitationData, citation.annotation))
    done()
  },
})
